import { debounce } from 'lodash'

import { deserializeSearchParams, serializeSearchParams } from 'components/SearchParams/helpers'
import backend from 'utils/backend'
import { deserializePaginationParams } from 'utils/helpers'
import history from 'utils/history'
import { trackSearchKeywords } from 'utils/mixpanel'

import { setSelectedFacets, setFacets, resetFacetFilters } from './facets'
import { resetFilters, setFilterByKey } from './filters'
import {
  resetPagination,
  SET_PAGE_NUMBER,
  setPageNumber,
  setViewsPerPage
} from './searchPagination'

export const UPDATE_SEARCH_RESULTS = 'UPDATE_SEARCH_RESULTS'
export const SEARCH_RESULTS_FAILURE = 'SEARCH_RESULTS_FAILURE'

export const CHANGE_SEARCH_TEXT = 'CHANGE_SEARCH_TEXT'
export const SET_SEARCH_LOADING = 'SET_SEARCH_LOADING'

export const changeSearchText = text => {
  return {
    type: CHANGE_SEARCH_TEXT,
    payload: { text }
  }
}

export const searchFundersByParams = source => async (dispatch, getState) => {
  const {
    facets: { selected },
    filters: { language, openToRequests, sortBy, medianGiftMin, medianGiftMax },
    searchPagination: { pageNumber, viewsPerPage },
    search: { text: searchText }
  } = getState()
  const searchParams = serializeSearchParams({
    selected,
    language,
    openToRequests,
    sortBy,
    searchText,
    pageNumber,
    viewsPerPage,
    medianGiftMin,
    medianGiftMax,
    statusToExclude: 'dissolved'
  })
  const url = `api/search/funders/${searchParams}`

  try {
    dispatch(setSearchLoading())
    history.push(`/search${searchParams}`)
    const { data: { results, count, facets: facetCountData } = {} } = await backend.get(url)

    if ((pageNumber - 1) * viewsPerPage > count) {
      // current page number is out of bounds for current viewsPerPage and needs to be recalculated
      // A new search is then initiated
      dispatch({
        type: SET_PAGE_NUMBER,
        payload: { pageNumber: Math.ceil(count / viewsPerPage) || 1 }
      })
      dispatch(searchFundersByParams())
    }

    dispatch(updateSearchResults(results, count))

    // update facet counts
    dispatch(setFacets(facetCountData))

    if (source === 'SEARCH_FORM') {
      if (searchText.length > 0) {
        await trackSearchKeywords({ searchText, count })
      }
    }
  } catch (error) {
    dispatch({ type: SEARCH_RESULTS_FAILURE, payload: { error } })
  }
}

export const debouncedFunderSearchByParams = debounce(dispatch => {
  dispatch(searchFundersByParams())
}, 500)

export const setSearchLoading = () => {
  return {
    type: SET_SEARCH_LOADING
  }
}

export const updateSearchResults = (results, count) => {
  return {
    type: UPDATE_SEARCH_RESULTS,
    payload: { results, count }
  }
}

export const resetSearch = () => dispatch => {
  dispatch(changeSearchText(''))
  dispatch(resetFilters())
  dispatch(resetFacetFilters())
  dispatch(resetPagination())
}

// read URL query params and set search text, filter and pagination settings
export const syncURL = params => dispatch => {
  const deserializedParams = deserializeSearchParams(params)

  // start from blank state
  dispatch(resetSearch())

  // dispatch filter data
  for (const [key, value] of Object.entries(deserializedParams)) {
    if (!['limit', 'offset', 'search'].includes(key)) {
      // disregard pagination data when dispatching filters
      dispatch(setFilterByKey({ [key]: value }, { triggerSearch: false }))
    }
  }

  // dispatch search text
  const { search } = deserializedParams
  if (search) {
    dispatch(changeSearchText(search))
  }

  // dispatch pagination data
  const { offset, limit } = deserializedParams
  if (offset && limit) {
    const { viewsPerPage, pageNumber } = deserializePaginationParams({ offset, limit })
    dispatch(setPageNumber(pageNumber))
    dispatch(setViewsPerPage(viewsPerPage))
  }

  // dispatch selected facets data
  const { selected } = deserializedParams
  if (selected && selected.length) {
    dispatch(setSelectedFacets(selected))
  }
}
